/* Ring builder — the gemstone DETAIL step (step 2b).

   Every card in step 2 already linked here (`RB.pages.stone` = /pages/gemstone);
   the page itself was never built, so the whole grid pointed at a 404. This is
   that page.

   THE 360 IS THE STULLER MP4, NOT A FRAME SEQUENCE. stone_spin.py's docstring
   argues for frames and it is right on the merits — a 32-frame sequence seeks
   instantly and is 40-70x smaller than the 2-11 MB source clip. But frames have
   to be HOSTED: 4,535 stones x 32 frames is ~145,000 files on Shopify's CDN,
   which is not a thing you can ship. So v1 plays the clip Stuller already hosts:
     - `preload="none"` + the still as poster, so the page costs ONE image until
       the customer actually asks for the spin. A 6 MB autoload on every stone
       view would be indefensible.
     - PLAY is the primary gesture and DRAG is secondary, which is the opposite
       of a frame viewer. These clips are not faststart (verified: `mdat` sits in
       the first 128 KB and `moov` is at the tail), so sequential playback is
       smooth while a cold scrub has to wait for the tail range request. Playing
       first warms the buffer and makes the drag feel right.
   The seam is `mountSpin360` — swap its innards for frames the day a sprite
   sheet exists and nothing else on this page changes.

   TWO KINDS OF STONE render here and only one of them has most of the fields.
   Serialized stones (natural + lab diamond) carry carat, the 4Cs, a lab report
   and a turntable. Calibrated lab COLOR carries mm, species and nothing else —
   no carat, no cert, no video (verified in the shards). Every row below is
   therefore conditional; a spec table full of blank rows is worse than a short
   one. */
(function () {
  'use strict';
  var RB = window.RingBuilder;
  if (!RB) return;                    // deferred order: engine must parse first
  var el = RB.el, money = RB.money;

  var cap = function (s) {
    return String(s || '').toLowerCase().replace(/(^|[\s-])\w/g, function (m) {
      return m.toUpperCase();
    });
  };

  var mm = function (v) { return v == null ? null : v.toFixed(2) + ' mm'; };

  /* -------------------------------------------------------- durability ---- */
  // The shard carries the wearability LABEL; the long-form sentence lives in
  // RB_STONES_META.tiers keyed by tier. Invert on the label rather than shipping
  // the prose twice — gem_durability.py stays the single source.
  function wearCopy(label) {
    var tiers = (window.RB_STONES_META || {}).tiers || {};
    var hit = null;
    Object.keys(tiers).forEach(function (k) {
      if (tiers[k] && tiers[k][0] === label) hit = tiers[k];
    });
    return hit ? hit[2] : null;
  }

  /* ------------------------------------------------------------ 360 ------- */
  /* `opts` serves the full-screen copy of this viewer (mountZoom below): `play` (spinning or held), `at` (open on
     the page's angle), `away` (resting while the photograph is on screen) and `hint: false`. The page passes none
     and gets exactly the viewer it had. Returns the controller mountZoom drives. */
  function mountSpin360(stage, url, poster, label, opts) {
    opts = opts || {};
    var v = el('video', {
      class: 'rb-spin__v', preload: 'none', muted: 'muted', playsinline: 'playsinline',
      loop: 'loop', poster: poster || null, tabindex: '0',
      'aria-label': label || 'Gemstone 360 view — press play, or drag to rotate'
    });
    v.muted = true;                   // attribute alone does not satisfy every browser
    stage.appendChild(v);

    var hint = el('span', { class: 'rb-spin__hint', text: 'Drag to rotate' });
    /* 🚨 THE CONTROL IS A CORNER TOGGLE, NOT A DISC OVER THE STONE (owner,
       2026-08-17: *"the play button is hiding the stone. It should play by default
       and pressing should pause it and maintain the ability to click and drag"*).
       It was a 5.6rem circle dead centre at z-index 2 — on the one screen whose whole
       job is looking at the gem, the control sat on the gem.

       ⚠️ AND IT WAS UNREACHABLE ON TOUCH ONCE PLAYING. The old CSS hid it with
       `[data-playing] { opacity: 0 }` and brought it back on `:hover` — a phone has
       no hover, so after the clip started there was no way to pause it at all. A
       corner control that is always visible fixes the owner's complaint and that
       silent bug with the same move; the glyph follows the video's own play/pause
       events, so drag-to-pause updates it too. */
    var play = el('button', {
      class: 'rb-spin__play', type: 'button', 'aria-label': 'Pause 360 view'
    }, [
      el('span', { class: 'rb-spin__tri', 'aria-hidden': 'true' }),
      el('span', { class: 'rb-spin__bars', 'aria-hidden': 'true' })
    ]);
    stage.appendChild(play);
    // the zoom's copy carries no hint, like the stone page's (snippets/stone-spin.liquid)
    if (opts.hint !== false) stage.appendChild(hint);

    /* 🚨 THE MEASURED FIT, LOOKED UP — NEVER COMPUTED HERE (owner, 2026-08-17: *"the
       gem looks small compared to the image, we need to apply the fix we have for the
       other gemstones"*). This stage drew the clip at zoom 1, so the stone rendered at
       Stuller's own framing while the still beside it is normalised to
       `stone_image.TARGET_FILL` 0.76. Measured on das 156444626: `fill` 0.503 against
       0.760 — the gem was **51% smaller** than its own photograph.

       `window.SRSpinFit` is published by `stone-hover-spin.js` precisely so a second
       surface cannot drift from the cards, and its own comment names the last time that
       happened: `snippets/stone-spin.liquid` kept a length-based estimate and clipped 4
       of 5 sampled product pages while the card beside it rendered clean. So the
       arithmetic is not repeated here — `verify_spin_fit.py` fails the build if a second
       copy of it appears.

       ⚠️ `ready()`, NOT a bare `zoomFor()`. The map is fetched on first use and is
       ~14,291 entries; measured on this page it is NOT loaded when the stone mounts, so
       an early `zoomFor` returns 0 — which is indistinguishable from "this clip was
       never measured" and would silently leave the gem small. That is the exact failure
       the API's own header describes.
       ⚠️ Zoom 0 is the honest "unmeasured" answer and must stay zoom 1, not a guess. */
    if (window.SRSpinFit && window.SRSpinFit.ready) {
      window.SRSpinFit.ready(function () {
        var z = window.SRSpinFit.zoomFor(url);
        if (z) stage.style.setProperty('--rb-spin-zoom', z.toFixed(4));
      });
    }

    var loaded = false, dragging = false, armed = false, x0 = 0, t0 = 0;
    /* THE SHOPPER'S CHOICE IS KEPT APART FROM WHAT THE CLIP IS DOING (owner 2026-10-05, the full-screen zoom).
       `want`: the shopper wants it spinning -- the play button, Space, a drag and the arrow keys set it, where they
       already started or stopped the clip. `away`: resting without changing that choice -- the page's clip while
       its copy is up in the zoom, the copy while the photograph is on screen. The stone page's viewer keeps the
       same two (snippets/stone-spin.liquid: userPaused, away), so both hand an angle back the same way. */
    var ctl = { video: v, want: false, away: !!opts.away };

    function load() {
      if (loaded) return Promise.resolve();
      loaded = true;
      stage.classList.add('is-loading');
      v.preload = 'auto';
      if (opts.at) {
        // the zoom's copy opens on the page's frame
        v.addEventListener('loadedmetadata', function () {
          try { v.currentTime = opts.at; } catch (e) {}
        }, { once: true });
      }
      v.src = url;
      return new Promise(function (res) {
        var done = function () { stage.classList.remove('is-loading'); res(); };
        v.addEventListener('loadeddata', done, { once: true });
        v.addEventListener('error', function () {
          // never leave an empty plate: fall back to the still
          stage.classList.remove('is-loading');
          stage.classList.add('is-failed');
          res();
        }, { once: true });
        v.load();
      });
    }

    play.addEventListener('click', function () {
      load().then(function () {
        if (stage.classList.contains('is-failed')) return;
        stage.classList.add('is-live');
        if (v.paused) { ctl.want = true; v.play().catch(function () {}); } else { ctl.want = false; v.pause(); }
        play.setAttribute('aria-label', v.paused ? 'Play 360 view' : 'Pause 360 view');
      });
    });
    // ONE SOURCE OF TRUTH FOR THE GLYPH: the video's own events. The button, a drag,
    // the keyboard and autoplay all change playback, and every one of them lands here
    // — so the icon cannot disagree with what the clip is doing.
    v.addEventListener('play', function () {
      stage.setAttribute('data-playing', 'true');
      play.setAttribute('aria-label', 'Pause 360 view');
    });
    v.addEventListener('pause', function () {
      stage.removeAttribute('data-playing');
      play.setAttribute('aria-label', 'Play 360 view');
    });

    /* 🚨 PLAYS BY DEFAULT (owner, 2026-08-17), WHICH REVERSES THIS FILE'S ORIGINAL
       COST DECISION — the header argued `preload="none"` because "a 6 MB autoload on
       every stone view would be indefensible". Measured today the clips are 4.10-4.63
       MB, mean 4.28 MB. What changed is not the number but where it is spent: this is
       the DETAIL page for ONE stone the shopper deliberately opened, not a grid of
       sixty, and the owner would rather pay it than put a disc over the gem. Step 2's
       cards are untouched and still load nothing until hovered.

       ⚠️ TWO WAYS AUTOPLAY CAN BE REFUSED AND BOTH MUST LEAVE A WORKING CONTROL.
       `play()` returns a promise that REJECTS under low-power mode, data saver, or a
       browser that does not accept muted autoplay — swallow it silently and the
       shopper gets a still with no visible way to start it. And
       `prefers-reduced-motion` is precisely the setting that exists to stop an
       unrequested looping animation, so it is honoured rather than overridden: no
       autoplay, button shows PLAY, everything still works on press.
       Either way the corner control is already on screen, so there is nothing to
       reveal and no second affordance to invent. */
    var quiet = false;
    try {
      quiet = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {}
    // the zoom's copy starts as the shopper left the page's clip (opts.play); the page exactly as before
    ctl.want = opts.play == null ? !quiet : !!opts.play;
    ctl.resume = function () {
      if (ctl.away || !ctl.want || !loaded || !v.paused || stage.classList.contains('is-failed')) return;
      var p = v.play();
      if (p && p.catch) {
        p.catch(function (err) {
          /* ⚠️ ONLY A REFUSAL CLEARS THE SHOPPER'S CHOICE (Codex review-muv27djv-vdei18). A pause while this request
             was still pending -- the zoom resting the clip, a slide change -- rejects it with AbortError, and a clip
             that is playing again by now was not refused; neither is the shopper choosing to hold the stone. */
          if ((err && err.name === 'AbortError') || !v.paused) return;
          // refused: the still stays, the corner button reads PLAY, drag still works
          ctl.want = false;
          stage.removeAttribute('data-playing');
          play.setAttribute('aria-label', 'Play 360 view');
        });
      }
    };
    // back from the zoom: the angle the shopper left there, then their choice (spinning or held)
    ctl.seek = function (t) {
      load().then(function () {
        if (stage.classList.contains('is-failed')) return;
        stage.classList.add('is-live');
        try { v.currentTime = t; } catch (e) {}
        ctl.resume();
      });
    };
    // the zoom's copy, when its slide comes on screen: load the clip (once, on the page's angle), then the choice
    ctl.wake = function () {
      ctl.away = false;
      load().then(function () {
        if (stage.classList.contains('is-failed')) return;
        stage.classList.add('is-live');
        ctl.resume();
      });
    };
    if (!ctl.want) {
      stage.removeAttribute('data-playing');
      play.setAttribute('aria-label', 'Play 360 view');
    }
    /* The page loads now unless reduced motion holds it back, as before. The zoom's copy loads only on screen: a
       copy resting behind the photograph waits for ctl.wake, so opening the photo never downloads the ~4 MB clip
       (Codex review-muv27djv-vdei18). A copy that is on screen loads even when held, to show the page's angle. */
    if (!ctl.away && (ctl.want || opts.at != null)) {
      load().then(function () {
        if (stage.classList.contains('is-failed')) return;
        stage.classList.add('is-live');
        ctl.resume();
      });
    }

    // DRAG TO SCRUB. One full rotation per 85% of the stage width.
    //
    // THE STONE FOLLOWS THE FINGER. Dragging right must turn the stone right,
    // which is what Blue Nile and every other 360 viewer does — a viewer that
    // moves opposite the pointer feels broken even when nobody can say why.
    // Stuller's turntable advances the stone LEFT as time increases, so the
    // drag has to run time BACKWARDS. This originally added, which inverted it,
    // and also disagreed with RB.mountSpin (the frame viewer) which subtracts.
    function seekBy(dx) {
      var d = v.duration;
      if (!d || !isFinite(d)) return;
      var t = t0 - (dx / (stage.clientWidth * 0.85)) * d;
      v.currentTime = ((t % d) + d) % d;
    }
    /* 🚨 A BARE TAP MUST NOT PAUSE, OR AUTOPLAY READS AS BROKEN ON A PHONE. This
       paused the clip on `pointerdown`, which was harmless while nothing was playing
       until you asked — and is not, now that it plays on arrival: `touch-action:
       pan-y` lets a finger scroll the page THROUGH the stage, and every one of those
       scrolls starts with a pointerdown on the stone. The spin would stop the first
       time a shopper scrolled past it, with no gesture they would call a click.
       So the drag ARMS on pointerdown and only TAKES OVER once the pointer has
       travelled `DRAG_MIN` px horizontally. Below that it is a tap and playback is
       left alone. 4px is the smallest value that a deliberate drag always exceeds and
       a stationary finger never does. */
    var DRAG_MIN = 4;
    stage.addEventListener('pointerdown', function (e) {
      if (e.target === play || play.contains(e.target)) return;
      armed = true; x0 = e.clientX;
      load().then(function () {
        if (stage.classList.contains('is-failed')) return;
        stage.classList.add('is-live');
      });
    });
    stage.addEventListener('pointermove', function (e) {
      if (!armed && !dragging) return;
      if (!dragging) {
        if (Math.abs(e.clientX - x0) < DRAG_MIN) return;
        // Crossing the threshold is the moment the shopper takes control: pause here,
        // and anchor the scrub at the frame that is on screen RIGHT NOW — reading
        // `currentTime` back on pointerdown would anchor at a frame the clip has
        // since played past, so the stone would jump before it followed the finger.
        dragging = true;
        ctl.want = false;
        v.pause();
        t0 = v.currentTime || 0;
        x0 = e.clientX;
        stage.classList.add('is-drag');
        try { stage.setPointerCapture(e.pointerId); } catch (err) {}
      }
      e.preventDefault();
      seekBy(e.clientX - x0);
    });
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      stage.addEventListener(ev, function (e) {
        // ⚠️ RELEASING DOES NOT RESUME. A shopper who dragged to an angle wants that
        // angle; restarting the loop under them would throw it away. The corner
        // button is how they start it again, and it already reads PLAY because the
        // pause above fired the video's own event.
        dragging = false; armed = false;
        stage.classList.remove('is-drag');
        try {
          if (e.pointerId != null && stage.hasPointerCapture(e.pointerId)) {
            stage.releasePointerCapture(e.pointerId);
          }
        } catch (err) {}
      });
    });

    // keyboard parity — the spin must be operable without a pointer
    v.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== ' ') return;
      e.preventDefault();
      load().then(function () {
        if (stage.classList.contains('is-failed')) return;
        stage.classList.add('is-live');
        if (e.key === ' ') {
          if (v.paused) { ctl.want = true; v.play().catch(function () {}); } else { ctl.want = false; v.pause(); }
          return;
        }
        ctl.want = false;
        v.pause();
        var d = v.duration;
        if (!d || !isFinite(d)) return;
        var step = d / 32;                        // 32 notional frames, as stone_spin.py
        // matches the drag convention above: Right turns the stone right
        var t = v.currentTime + (e.key === 'ArrowRight' ? -step : step);
        v.currentTime = ((t % d) + d) % d;
      });
    });
    return ctl;
  }

  /* ------------------------------------------------------- full screen ---- */
  /* 🚨 THE 360 AND THE PHOTOGRAPH OPEN FULL SCREEN, IN THE SITE'S OWN PHOTO ZOOM (owner 2026-10-05: "the ability
     to zoom on the video and picture when selecting the stone, similar to what we did when we added the expansion
     option to video and the ability to select the next picture"). It is the zoom a stone's product page opens
     (snippets/stone-spin.liquid, LIVE 2026-09-29): the 360 is slide 1 and still turns under a drag, the photograph
     is slide 2, with the zoom's own arrows, counter, swipe and close, both at the photo's zoom size there (800 px).
     The button is the one the builder's ring photos carry (RB.mountAngleExpansion: the product photo's expansion
     arrows and `rb-view__expand` look), on hover on a computer and always on a touch screen. As on the product
     page, a tap on the 360 on a touch screen opens it too (a mouse click there still does nothing), and so does a
     click on the photograph.
     assets/lightbox.js hands out its PhotoSwipe instance as window.SRLightbox; sections/ring-builder.liquid loads it
     on this page. Until it has announced itself -- or on a cached page that does not load it -- the buttons stay
     hidden and nothing opens: the page as before. */
  var ZOOM_PX = 800;
  var ZOOM_ICON = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="var(--icon-stroke-width, 1)" stroke="currentColor" class="icon" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/></svg>';

  function zoomButton(label) {
    var b = el('button', { class: 'rb-view__expand product__media-icon', type: 'button', hidden: true,
                           'aria-label': label, 'aria-haspopup': 'dialog' });
    b.innerHTML = ZOOM_ICON;
    // a press on the button is not a drag, a tap or a key for the viewer under it
    ['pointerdown', 'pointerup', 'keydown'].forEach(function (name) {
      b.addEventListener(name, function (e) { e.stopPropagation(); });
    });
    return b;
  }

  function zoomReady() {
    var lb = window.SRLightbox;
    return !!(lb && lb.options && typeof lb.loadAndOpen === 'function' && typeof lb.on === 'function'
              && typeof lb.off === 'function');
  }

  // `spin` is the page's 360 (mountSpin360) or null; `shotStage` the plate holding the photograph (the stage itself
  // for a stone filmed by type, which has no clip) or null.
  function mountZoom(root, stone, spin, spinStage, shotStage) {
    var shotImg = shotStage ? shotStage.querySelector('img') : null;
    if (!spin && !shotImg) return;
    var spinBtn = spin ? zoomButton('Open the 360° view full screen') : null;
    var shotBtn = shotImg ? zoomButton('Open the photo full screen') : null;
    var zoom = null;                       // the open zoom
    var token = {};                        // marks this page's slides

    function ready() {
      var ok = zoomReady();
      if (ok) document.removeEventListener('sr:lightbox', ready);
      if (spinBtn) spinBtn.hidden = !ok;
      if (shotBtn) shotBtn.hidden = !ok;
    }

    // The 360's slide: a copy of the page's viewer (the same clip, measured fit, play button and drag), opened on
    // the page's angle and the shopper's choice. It rests until its slide is the one on screen.
    function slide() {
      var box = el('div', { class: 'rb-zoom-slide' });
      // the builder's colours are declared on `.rb`, and the zoom is drawn outside it
      var cs = window.getComputedStyle(root);
      ['--rb-ink', '--rb-muted', '--rb-rule', '--rb-gold', '--rb-plate'].forEach(function (k) {
        var val = cs.getPropertyValue(k);
        if (val) box.style.setProperty(k, val.trim());
      });
      var st = el('div', { class: 'rb-stage rb-spin' });
      zoom.copy = mountSpin360(st, stone.video, stone.img, stone.title + ' — 360 view',
                               { play: zoom.want, at: zoom.t, away: !zoom.seen, hint: false });
      if (stone.img) {
        st.insertBefore(el('img', { class: 'on rb-spin__still', src: stone.img, alt: stone.title,
                                    draggable: 'false' }), st.firstChild);
      }
      box.appendChild(st);
      return box;
    }

    function onSpin() {
      var p = zoom && zoom.lb.pswp;
      var d = p && p.currSlide && p.currSlide.data;
      return !!(d && d.rbZoom === token && d.type === 'rb-spin');
    }
    function onContentLoad(e) {
      var d = e.content && e.content.data;
      if (!zoom || !d || d.rbZoom !== token || d.type !== 'rb-spin') return;
      e.preventDefault();
      e.content.element = slide();
    }
    // A drag on the stone turns it: PhotoSwipe must not also read that press as a swipe, a drag to close or a tap.
    function onPointerDown(e) {
      var t = e.originalEvent && e.originalEvent.target;
      if (t && t.closest && t.closest('.rb-zoom-slide')) e.preventDefault();
    }
    // With the 360 focused, the arrow keys turn the stone (its own keys), not the slide.
    function onKeydown(e) {
      var k = e.originalEvent && e.originalEvent.key;
      var a = document.activeElement;
      if ((k === 'ArrowLeft' || k === 'ArrowRight') && a && a.closest && a.closest('.rb-zoom-slide')) {
        e.preventDefault();
      }
    }
    // The 360 loads and plays only while its slide is the one on screen (ctl.wake); behind the photograph it rests.
    function onChange() {
      if (!zoom) return;
      var here = onSpin();
      if (here) zoom.seen = true;
      var c = zoom.copy;
      if (!c) return;
      if (here) c.wake();
      else { c.away = true; c.video.pause(); }
    }
    // Back on the page, the viewer takes the slide's angle and the shopper's choice there (spinning, or held where
    // they turned it). A zoom that never showed the 360 leaves the page's clip as it was.
    function onClose() {
      var z = zoom;
      if (!z || z.closed) return;
      z.closed = true;
      var c = z.copy;
      if (c) { c.away = true; c.video.pause(); }
      if (!spin) return;
      spin.away = false;
      if (z.seen && c && c.video.readyState >= 1) {
        spin.want = c.want;
        spin.seek(c.video.currentTime);
      } else {
        spin.resume();
      }
    }
    function onDestroy() {
      var z = zoom;
      zoom = null;
      if (!z) return;
      z.lb.off('contentLoad', onContentLoad);
      z.lb.off('pointerDown', onPointerDown);
      z.lb.off('keydown', onKeydown);
      z.lb.off('change', onChange);
      z.lb.off('close', onClose);
      z.lb.off('destroy', onDestroy);
    }

    function open(index) {
      if (zoom || window.pswp || !zoomReady()) return;
      var lb = window.SRLightbox;
      var w = ZOOM_PX, h = ZOOM_PX;
      if (shotImg && shotImg.naturalWidth && shotImg.naturalHeight) {
        var k = Math.min(1, ZOOM_PX / Math.max(shotImg.naturalWidth, shotImg.naturalHeight));
        w = Math.round(shotImg.naturalWidth * k);
        h = Math.round(shotImg.naturalHeight * k);
      }
      var items = [];
      if (spin) items.push({ type: 'rb-spin', rbZoom: token, width: w, height: h, alt: stone.title + ' — 360 view' });
      // `element` lets the zoom grow out of the page's photograph and shrink back into it, as the product page's does
      if (shotImg) items.push({ src: stone.img, msrc: shotImg.currentSrc || stone.img, width: w, height: h,
                                alt: stone.title, element: shotStage });
      zoom = { lb: lb, want: spin ? spin.want : false, t: spin ? (spin.video.currentTime || 0) : 0,
               seen: !!spin && index === 0, copy: null, closed: false };
      if (spin) { spin.away = true; spin.video.pause(); }      // the page's clip rests while the zoom is up
      lb.on('contentLoad', onContentLoad);
      lb.on('pointerDown', onPointerDown);
      lb.on('keydown', onKeydown);
      lb.on('change', onChange);
      lb.on('close', onClose);
      lb.on('destroy', onDestroy);
      if (!lb.loadAndOpen(index, items)) {
        onClose();
        onDestroy();
      }
    }

    if (spinBtn) {
      spinBtn.addEventListener('click', function (e) { e.stopPropagation(); open(0); });
      spinStage.appendChild(spinBtn);
      /* A TAP ON A TOUCH SCREEN OPENS IT, as on the product page. A drag still turns the stone, a scroll that starts
         on it still scrolls, and a mouse click still does nothing.
         ⚠️ OPENED ON THE TAP'S CLICK, NOT ON pointerup: a phone fires its compatibility mousedown after pointerup,
         and that would focus the video (tabindex 0) and take focus back off the zoom that had just opened (measured
         on the product page; snippets/stone-spin.liquid opens on the click for the same reason). */
      var tap = null, tapOpen = 0;
      spinStage.addEventListener('pointerdown', function (e) {
        tapOpen = 0;
        tap = (e.target.closest && e.target.closest('button')) ? null
            : { x: e.clientX, y: e.clientY, at: Date.now() };
      });
      spinStage.addEventListener('pointercancel', function () { tap = null; });
      spinStage.addEventListener('pointerup', function (e) {
        var t = tap;
        tap = null;
        if (t && e.pointerType !== 'mouse' && Math.abs(e.clientX - t.x) < 10 && Math.abs(e.clientY - t.y) < 10
            && Date.now() - t.at < 500) tapOpen = Date.now();
      });
      spinStage.addEventListener('click', function () {
        var t = tapOpen;
        tapOpen = 0;
        if (t && Date.now() - t < 1000) open(0);
      });
    }
    if (shotBtn) {
      shotBtn.addEventListener('click', function (e) { e.stopPropagation(); open(spin ? 1 : 0); });
      shotStage.appendChild(shotBtn);
      // the photograph itself opens it, as the product page's does
      shotStage.addEventListener('click', function () { open(spin ? 1 : 0); });
    }
    document.addEventListener('sr:lightbox', ready);
    ready();
  }

  /* -------------------------------------------------------- spec table ---- */
  function specRows(s) {
    var rows = [];
    var add = function (k, val) { if (val != null && val !== '') rows.push([k, val]); };

    add('Shape', cap(s.shape));
    add('Type', (s.origin === 'lab' ? 'Lab-grown ' : 'Natural ') + (s.species || 'gemstone'));
    if (s.carat) add('Carat weight', s.carat.toFixed(2) + ' ct');
    if (s.l) {
      add('Measurements', s.w && Math.abs(s.w - s.l) > 0.005
        ? s.l.toFixed(2) + ' × ' + s.w.toFixed(2) + ' mm'
        : mm(s.l));
    }
    // 🚨 THE REPORTED DEFECT: this printed `custom.colour_grade` raw, which on a
    // coloured stone is the supplier's trade code — the owner's *"Bl"*. One rule,
    // shared with the cards and the compare overlay: RB.colourLabel (on the ENGINE,
    // because ring-builder-stones.js is not loaded on this page — see that helper).
    add('Color', RB.colourLabel(s));
    add('Clarity', s.clarity);
    add('Cut', s.cut);
    // Mohs is a species property, so it rides in the shared meta rather than as
    // a column repeated 4,535 times. Absent until rb-stones-meta.js is rebuilt,
    // hence the guard — a missing row is correct, an "undefined" row is not.
    var mohs = ((window.RB_STONES_META || {}).mohs || {})[s.species];
    if (mohs) add('Hardness', mohs + ' on the Mohs scale');
    add('Wearability', s.wear);
    return rows;
  }

  /* -------------------------------------------------------------- mount --- */
  function mount(root, build, stone) {
    var body = root.querySelector('[data-rb-body]');
    if (!body) return;

    // the masthead heading ships visually hidden on this page (`rb-h1--pending`,
    // sections/ring-builder.liquid) so it does not jump into the column when the
    // stone resolves; a stone that cannot be found never moves it, so unhide here
    var pendingH1 = root.querySelector('h1.rb-h1--pending');
    if (!stone) {
      if (pendingH1) pendingH1.classList.remove('rb-h1--pending');
      body.innerHTML = '';
      body.appendChild(el('div', { class: 'rb-empty' }, [
        el('p', { text: 'We could not find that gemstone. It may have just sold — every ' +
                        'serialized stone here is one of one.' }),
        el('a', { class: 'btn btn-primary', style: 'margin-top:1.6rem',
          href: RB.href(RB.stonesPage(build),
                        build, { stone: null }),
          text: 'Back to all gemstones' })
      ]));
      return;
    }

    try { document.title = stone.title + ' | Sapphire Row'; } catch (e) {}

    body.innerHTML = '';

    /* ---------------- media ---------------- */
    var stage = el('div', { class: 'rb-stage rb-spin' });
    var spin = null;
    if (stone.video) {
      spin = mountSpin360(stage, stone.video, stone.img, stone.title + ' — 360 view');
      if (stone.img) {
        stage.insertBefore(el('img', { class: 'on rb-spin__still', src: stone.img,
          alt: stone.title, draggable: 'false' }), stage.firstChild);
      }
    } else if (stone.img) {
      stage.appendChild(el('img', { class: 'on', src: stone.img, alt: stone.title }));
    } else {
      // 2 serialized stones ship no media at Stuller at all — an edge case, not a bug
      stage.appendChild(el('span', { class: 'rb-noimg',
        text: 'Photography for this stone is on its way. Ask us and we will send it.' }));
    }
    // `rb-detail__media` is what the CSS grids when a still is present — see the
    // `:has(.rb-detail__shot)` rule, the same technique the gem preview page uses
    // to pair its own spin with its photograph.
    var media = el('div', { class: 'rb-detail__media' }, [stage]);
    /* 🚨 THE PHOTOGRAPH STAYS ON THE PAGE ALONGSIDE THE CLIP (owner, 2026-08-17: *"We
       should have the still image on the page along with the video that autoplays"*).
       Until now the still existed only as an UNDERLAY inside the stage and
       `.rb-spin.is-live` faded it to 0 the moment the clip had frames — so the moment
       the spin worked, the photograph was gone. It is the branded, normalised,
       face-up shot; the clip is Stuller's raw turntable. The shopper should have both.

       ⚠️ THE UNDERLAY STAYS TOO, and it is not a duplicate: it is what stops the plate
       being briefly empty while ~4 MB of non-faststart clip is fetched (the poster
       cannot cover that on its own). Same `src`, so the browser serves the second one
       from cache — one download, two places.
       ⚠️ ONLY WHERE THERE IS A CLIP. With no video the stage already shows this exact
       image, and rendering it twice would read as a duplicate rather than as a pair. */
    /* THE PHOTOGRAPH SITS ON A PLATE OF ITS OWN (`rb-stage`, owner 2026-10-05), so its full-screen button takes
       the 360's corner and hover rules. It draws as before: a square on the plate colour, the photo contained. */
    var shot = null;
    if (stone.video && stone.img) {
      shot = el('div', { class: 'rb-stage' }, [
        el('img', { class: 'on', src: stone.img, alt: stone.title, loading: 'lazy', draggable: 'false' })
      ]);
      media.appendChild(el('figure', { class: 'rb-detail__shot' }, [
        shot,
        el('figcaption', { text: 'Photographed still' })
      ]));
    }
    if (!stone.video) {
      media.appendChild(el('p', { class: 'rb-spin__none',
        text: stone.img ? 'This stone is calibrated stock, so it is photographed by type ' +
                          'rather than individually.' : '' }));
    }
    // full screen: the 360 and the photograph, or the photograph alone on a stone with no clip
    mountZoom(root, stone, spin, spin ? stage : null, shot || (!stone.video && stone.img ? stage : null));

    /* ---------------- info: A PRODUCT PAGE'S COLUMN ----------------
       Owner, 2026-09-02: "like how /products/12-01-ct-lab-grown-diamond-… looks, but
       details showing in plain sight. The link to the cert should be more visible
       too." So this column follows `.product__info-container` — breadcrumb · title ·
       one-line sub · price · the lab report · CTA · details — with two deliberate
       departures: the spec table is EXPANDED where the PDP folds it into an
       accordion, and the report is a bordered row rather than a body-text sentence,
       because it is the single most trust-bearing element on the page. */
    var info = el('div', { class: 'rb-detail__info rb-pdp' });
    /* THE METAL HALF IS A "SETTING" ON A RING AND A "MOUNTING" ON A PENDANT OR
       EARRING — `RB.KINDS[kind].mount`, the word every other step uses. These three
       strings hardcoded "setting" and were the only place in the pendant flow a
       shopper met the ring word (Codex, 2026-09-02). */
    var mountWord = RB.KINDS[RB.kind(build)].mount;

    info.appendChild(RB.crumb([
      { href: '/', text: 'Home' },
      { href: RB.href(RB.stonesPage(build), build, { stone: null }), text: 'Choose a gemstone' },
      { text: stone.title }
    ]));
    /* ONE H1 per page (owner page-SEO rule). The section renders a static heading
       in the masthead; the real subject of this page is the stone, so that heading
       is retitled AND MOVED into this column, where a product page keeps its title.
       Moving the node keeps the count at one where a second heading would not.
       ⚠️ Stashed on `root`: a re-mount clears `body`, which by then holds the h1, so
       `querySelector` would find nothing and the page would lose its only h1. The
       node object survives the clear and is simply appended again.
       ⚠️ The theme's scroll-reveal classes come off. They fade the heading in on an
       IntersectionObserver, and a heading that arrives with the column's content
       should be there when the column is — not a beat later. */
    var h1 = root.__rbH1 || root.querySelector('h1');
    if (h1) {
      h1.textContent = stone.title;
      h1.classList.remove('scroll-trigger', 'animate--fade-in', 'scroll-trigger--offscreen',
                          'rb-h1--pending');
      h1.classList.add('rb-pdp__title');
      h1.removeAttribute('style');
    } else {
      // the section's Heading setting can be blanked in the theme editor, and then
      // Liquid renders no h1 at all — so the fallback MAKES the page's one h1 rather
      // than an h2 that leaves it with none (Codex, 2026-09-02)
      h1 = el('h1', { class: 'h2 rb-pdp__title', text: stone.title });
    }
    root.__rbH1 = h1;
    info.appendChild(h1);

    var fitLine = null;
    if (build.seat) {
      var ok = RB.fits(stone, build.seat, build.style);
      fitLine = el('p', { class: 'rb-detail__fit' + (ok ? ' is-ok' : ' is-bad'),
        text: ok ? 'Fits your ' + RB.seatLabel(build.seat) + ' ' + mountWord
                 : 'Does not fit your ' + RB.seatLabel(build.seat) + ' ' + mountWord +
                   ' — choose another stone, or change the ' + mountWord + ' size.' });
      info.appendChild(fitLine);
    }

    info.appendChild(el('div', { class: 'rb-detail__price rb-pdp__price', text: money(stone.price) + (RB.pairQty(build) > 1 ? ' each · ' + money(stone.price * RB.pairQty(build)) + ' for two stones' : '') }));

    /* THE LAB REPORT, IN PLAIN SIGHT. It was a muted 12px sentence under the spec
       table ("GIA report 2231546936 View the report"); it is a bordered row now, the
       whole row a link where a report URL exists, placed between the price and the
       CTA so it is read before the commitment rather than found after it. */
    if (stone.lab && stone.cert) {
      /* EGL USA: NO LINK, AN INSTRUCTION (owner, 2026-09-17). The feed's eglusa.com results page answers "unable to
         locate" for these numbers; EGL USA's Los Angeles lookup finds them but is plain http only, and the owner chose
         text over a button to a "Not secure" page -- the same note as snippets/stone-certificate.liquid. */
      var egl = /^egl\s*usa$/i.test(String(stone.lab).trim());
      var certHref = egl ? null : stone.certUrl;
      var certKids = [
        el('span', { class: 'rb-cert__k', text: stone.lab + ' report' }),
        el('span', { class: 'rb-cert__n', text: stone.cert }),
        certHref ? el('span', { class: 'rb-cert__go', text: 'View the report ↗' }) : null,
        egl ? el('span', { class: 'rb-cert__note', style: 'flex-basis: 100%; font-size: 1.3rem; font-weight: 400; color: var(--rb-muted);',
                           text: 'To check this report, visit eglusa.us/verify/search and enter the report number without the \u201cUS\u201d at the start.' }) : null
      ];
      info.appendChild(certHref
        ? el('a', { class: 'rb-cert', href: certHref, target: '_blank',
                    rel: 'noopener nofollow' }, certKids)
        : el('div', { class: 'rb-cert' }, certKids));
    }

    // built here, appended AFTER the CTA — details sit under the action on a PDP
    var tbl = el('dl', { class: 'rb-spec' });
    specRows(stone).forEach(function (r) {
      tbl.appendChild(el('dt', { text: r[0] }));
      tbl.appendChild(el('dd', { text: r[1] }));
    });

    /* ---------------- CTA ---------------- */
    // Two entry orders are legitimate. Most customers arrive with a setting
    // already chosen; some land on a stone from search and have none. Sending
    // the second group to review would show them half a ring, so they go to
    // step 1 with the stone carried in the URL.
    var hasSetting = !!(build.style && build.seat);
    // snap the seat to this stone — see RB.bestSeat. Without it the review page
    // inherits whatever seat was picked in step 1b, which may be a different
    // shape entirely.
    var seat = (build.style && RB.bestSeat(build.style, stone)) || null;
    /* 🚨 STEP 3 IS OFFERED, NOT FORCED (owner, 2026-08-20, second ruling: "there
       should be two options before proceeding — proceed with default side stones, or
       select side stones"). This SUPERSEDES the first ruling of the same day ("it
       skipped the 3rd step, needing to go back and pick after"), and that ruling's
       reasoning is still the reason this is safe: what it objected to was a default
       applied SILENTLY on the way to review. `RB.renderSideChoice` names the stone,
       prices the set and offers the other door on this screen, so the default is
       declined before it applies rather than discovered afterwards.
       The single CTA below is what a build with NO resolvable default still gets —
       and it still routes into step 3, because then there is nothing to proceed with.
       ⚠️ KEYED ON THE SNAPPED SEAT, NEVER `build.seat`. The side spec is a property of
       the CENTRE seat and this line is where that seat is decided — reading the old
       one would test a seat the shopper is in the act of leaving, and on this style
       the two carry different side sizes (Oval 4x3 through 8x6). */
    var sideNext = hasSetting && seat && RB.sideSeat(build.style, seat);
    var next = !hasSetting ? RB.pages.settings
             : sideNext ? RB.sidePage(build)
             : RB.pages.review;
    var cta = el('a', {
      class: 'btn btn-primary rb-detail__cta rb-pdp__cta',
      href: RB.href(next, build, { stone: stone.vid, shape: stone.shape,
                                   origin: stone.origin,
                                   seat: seat || build.seat || null,
                                   // `for` is a MODE and lives outside FIELDS, so it
                                   // has to be named here or this lands on the
                                   // ordinary centre grid
                                   'for': sideNext ? 'side' : null }),
      // "setting" is the RING word. This CTA is the stone-first path's hinge and
      // an earring shopper met it reading "Choose a setting", two clicks after a
      // button that said "Choose a Mounting" — see the same three-names-for-one-
      // thing note on RB.KINDS.
      text: !hasSetting ? 'Choose a ' + RB.KINDS[RB.kind(build)].mount + ' for it →'
           : sideNext ? ((RB.secondStone && RB.secondStone(build.style, sideNext) && RB.pairQty(build) === 1)
                         // ONE stone on a second-stone design (RB.secondStone; guarded against the engine's previous copy)
                         ? 'Choose this gemstone — second stone next →'
                         : 'Choose this gemstone — side stones next →')
           : 'Choose this gemstone →'
    });
    if (build.style && !seat) {
      // the style cannot hold this stone at all — say so rather than letting it
      // through to a cart line the bench would have to reject
      cta.removeAttribute('href');
      cta.setAttribute('aria-disabled', 'true');
      cta.classList.add('is-disabled');
      cta.textContent = 'Will not fit your ' + mountWord;
    }
    if (!stone.available) {
      cta.removeAttribute('href');
      cta.setAttribute('aria-disabled', 'true');
      cta.classList.add('is-disabled');
      cta.textContent = 'Just sold';
    }
    info.appendChild(cta);

    /* ── THE TWO DOORS ────────────────────────────────────────────────────────
       Resolved async because the default needs its shard, so the page never waits:
       the single CTA above is already correct and already links into step 3, and the
       choice block REPLACES it only once we know there is a default to offer.
       🚨 THAT ORDER IS THE FAILURE MODE, NOT A NICETY. Drawing "proceed with default
       side stones" first and filling it in later would offer a door to review before
       anything had checked a stone fits — and `defaultSideStone` legitimately returns
       null on the seats where no colourless diamond does, where the honest screen is
       the one that only offers step 3.
       ⚠️ GUARDED ON THE STONE STILL BEING THE ONE ON SCREEN. The resolve outlives a
       fast second click, and a late block naming the previous stone's sides is worse
       than none. */
    if (sideNext && stone.available) {
      (function (forStone) {
        RB.sideChoice(build, sideNext).then(function (choice) {
          if (!choice) return;                     // no default — keep the single CTA
          if (forStone !== stone.vid) return;      // a different stone is on screen now
          if (!cta.parentNode) return;
          var block = RB.renderSideChoice(build, choice, {
            stone: stone.vid, shape: stone.shape, origin: stone.origin,
            seat: seat || build.seat || null
          });
          cta.parentNode.replaceChild(block, cta);
        });
      })(stone.vid);
    }

    /* DETAILS, EXPANDED. The PDP folds these into a "Product Details" accordion; the
       owner wants them open ("details showing in plain sight"). */
    info.appendChild(el('h2', { class: 'rb-pdp__h', text: 'Details' }));
    info.appendChild(tbl);

    var longform = wearCopy(stone.wear);
    if (longform && stone.wear && stone.wear.indexOf('Excellent') < 0) {
      info.appendChild(el('div', { class: 'rb-note' }, [
        el('strong', { text: stone.wear + '. ' }),
        el('span', { text: longform })
      ]));
    }

    info.appendChild(el('p', { class: 'rb-detail__back' }, [
      el('a', { href: RB.href(RB.stonesPage(build),
                              build, { stone: null }),
        text: '← Back to all gemstones' })
    ]));
    if (!hasSetting) {
      info.appendChild(el('p', { class: 'rb-detail__note',
        text: 'You have not picked a ' + mountWord + ' yet — we will take you there next and ' +
              'keep this stone with you.' }));
    }

    body.appendChild(media);
    body.appendChild(info);
  }

  RB.mountStoneDetail = mount;
})();
